import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useHistory } from 'react-router-dom';
import { Box, Spacer, Button } from '@eatclubapp/ec-component-library';
import React, { useEffect, useRef } from 'react';
import useStyles, { getModalInputStyle, getModalStyle } from './AudienceTargetingModalStyles';
import { Modal } from '../../../components/Modal';
import NewCustomersSvg from '../../../assets/icons/new_customers.svg';
import WinBackCustomersSvg from '../../../assets/icons/magnet.svg';
import GrowDatabaseSvg from '../../../assets/icons/bar_graph.svg';
import RewardRegularsSvg from '../../../assets/icons/reward_star.svg';
import CheckInCircle from '../../../assets/icons/check_in_circle_bg.svg';
import { formatDate } from '../../../utils/helpers';
import Mixpanel from '../../../lib/mixpanel';

const AUDIENCE_TARGETING_PATH = '/audience-targeting';

const AudienceTargetingModal = ({ isOpen, onClose, onConfirm, onActivate, audienceTargeting }) => {
  const classes = useStyles();
  const history = useHistory();
  const hasBeenVisibleRef = useRef(false);

  const targetingOptions = [
    {
      key: 'newCustomers',
      title: 'Welcome new diners',
      description:
        "Drive first-time visits with exclusive offers for customers who've never dined at your venue.",
      Image: NewCustomersSvg,
      backgroundColor: '#FCFCCF',
      enabled: audienceTargeting?.data?.attractNewBoost?.enabled,
      enabledDate: audienceTargeting?.data?.attractNewBoost?.startDate,
    },
    {
      key: 'winBackCustomers',
      title: 'Win back customers',
      description: "Bring back diners who haven't visited in 90+ days with re-engagement offers.",
      Image: WinBackCustomersSvg,
      backgroundColor: '#D9D5F0',
      enabled: audienceTargeting?.data?.winBackBoost?.enabled,
      enabledDate: audienceTargeting?.data?.winBackBoost?.startDate,
    },
    // {
    //   key: 'growDatabase',
    //   title: 'Grow your database',
    //   description:
    //     'Obtain marketing opt-ins to build your contact list and own your customer relationships.',
    //   Image: GrowDatabaseSvg,
    //   backgroundColor: '#788229',
    //   enabled: false,
    // },
    // {
    //   key: 'rewardRegulars',
    //   title: 'Reward frequent visitors',
    //   description:
    //     'Reward your regulars and increase loyalty by targeting customers who visit often.',
    //   Image: RewardRegularsSvg,
    //   backgroundColor: '#EAE2BB',
    //   enabled: false,
    // },
  ];

  const isLoading = audienceTargeting?.fetching;
  const hasBoostsEnabled = audienceTargeting?.data?.boosts?.some((b) => b.enabled);
  const hasEverEnabledBoost = localStorage.getItem('hasEverEnabledAudienceTargeting') === 'true';

  // For existing partners who already have a boost active and set it to false, we don't want to show the modal.
  useEffect(() => {
    if (!isLoading && hasBoostsEnabled) {
      localStorage.setItem('hasEverEnabledAudienceTargeting', 'true');
    }
  }, [isLoading, hasBoostsEnabled]);

  const canShow = isOpen && !hasBoostsEnabled && !hasEverEnabledBoost;
  const shouldShow = canShow && (!isLoading || hasBeenVisibleRef.current);

  useEffect(() => {
    if (shouldShow) {
      hasBeenVisibleRef.current = true;
    }
    if (!isOpen) {
      hasBeenVisibleRef.current = false;
    }
  }, [shouldShow, isOpen]);

  const handleMaybeLater = () => {
    Mixpanel.track('Audience targeting modal - Maybe later');
    onClose();
  };

  const handleActivate = (type) => {
    Mixpanel.track('Audience targeting modal - Activate now', { type });
    onClose();
    history.push(AUDIENCE_TARGETING_PATH);
    if (onActivate) {
      onActivate(type);
    }
  };

  const handleDiscoverMore = () => {
    Mixpanel.track('Audience targeting modal - Discover more');
    onClose();
    history.push(AUDIENCE_TARGETING_PATH);
    if (onConfirm) {
      onConfirm();
    }
  };

  return (
    <Modal
      cancelLabel='Maybe later'
      confirmLabel='Discover more'
      heading=''
      isOpen={shouldShow}
      onClose={onClose}
      onConfirm={handleDiscoverMore}
      onCancel={handleMaybeLater}
      hideFooter={false}
      inputStyle={getModalInputStyle()}
      style={getModalStyle()}
    >
      <Box className={classes.content}>
        <Box className={classes.tag}>NEW FEATURE</Box>

        <Box className={classes.title}>
          Target the right customers to drive more diners into your restaurant
        </Box>

        <Box className={classes.introText}>
          Reach more EatClub customers and drive more visits to your venue with audience targeting,
          now live in your portal.
        </Box>

        <Spacer direction='vertical' gap={24} className={classes.optionsList}>
          {targetingOptions.map((option, index) => (
            <React.Fragment key={option.key}>
              <Spacer gap={20} className={classes.optionRow}>
                <Box
                  className={classes.iconContainer}
                  style={{ backgroundColor: option.backgroundColor }}
                >
                  {option.Image && <option.Image className={classes.imageIcon} />}
                </Box>
                <Spacer direction='vertical' className={classes.optionContent}>
                  <Box className={classes.optionTitle}>{option.title}</Box>
                  <Box className={classes.optionDescription}>{option.description}</Box>
                </Spacer>
                <Box className={classes.optionAction}>
                  {option.enabled && option.enabledDate ? (
                    <Spacer gap={8} className={classes.enabledStatus}>
                      <CheckInCircle />
                      <Box className={classes.enabledText}>
                        Enabled on {formatDate(option.enabledDate, 'DD MMM')}
                      </Box>
                    </Spacer>
                  ) : (
                    <Box className={classes.buttonWrapper}>
                      <Button
                        type='secondary'
                        onClick={() => handleActivate(option.key)}
                        style={{
                          button: {
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: '10px',
                            backgroundColor: 'transparent',
                            height: '40px',
                            padding: '10px 24px',
                            textAlign: 'center',
                            color: '#313131',
                            fontSize: '14px',
                            fontStyle: 'normal',
                            fontWeight: 400,
                            lineHeight: 'normal',
                            leadingTrim: 'both',
                            textEdge: 'cap',
                          },
                          buttonHover: {
                            backgroundColor: 'white',
                            filter: 'none',
                          },
                          text: {
                            marginTop: '-2px',
                          },
                        }}
                      >
                        Activate now
                      </Button>
                    </Box>
                  )}
                </Box>
              </Spacer>
              {index < targetingOptions.length - 1 && <Box className={classes.divider} />}
            </React.Fragment>
          ))}
        </Spacer>
      </Box>
    </Modal>
  );
};

AudienceTargetingModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func,
  onActivate: PropTypes.func,
  audienceTargeting: PropTypes.shape({
    fetching: PropTypes.bool,
    data: PropTypes.shape({
      enabled: PropTypes.bool,
      startDate: PropTypes.string,
    }),
  }),
};

const mapStateToProps = (state) => ({
  audienceTargeting: state.audienceTargeting,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(AudienceTargetingModal);
